/**
 * "Never write to this address again", from the thread where the person
 * actually said it.
 *
 * An opt-out does not arrive as a click on a link. It arrives as a sentence in
 * a reply, and until now the only way to honour it was to read the address,
 * open /marketing/suppression in another tab, pick the kind, retype the
 * address and save. Retyped addresses are how people stay on lists they asked
 * to leave.
 *
 * The address is taken from the first INBOUND message, never from the thread
 * itself: a thread's own subject line has no address, and the outbound side is
 * our own mailbox, which is not who is asking to be left alone.
 *
 * It is a circle in the header rather than the wide amber pill it used to be.
 * The pill was the widest thing on the links strip and read as a suggestion,
 * which is the wrong register for an action nobody can undo - the confirmation
 * dialog is where the weight belongs, and it is still there. The amber stays,
 * because among seven neutral outlines the one that ends a relationship should
 * not look like archive.
 */
import { useState } from "react";
import { Ban, Check, Loader2 } from "lucide-react";
import { ConfirmDialog, cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { addSuppression } from "../../../../lib/marketing";
import type { MailThread } from "../../../../lib/mail";
import { IconBtn, useToast } from "../../../ui";

/** The address that asked us to stop, or null when nobody has written to us. */
export function inboundAddress(thread: MailThread): string | null {
  return (thread.messages ?? []).find((m) => m.direction === "inbound")?.fromAddress ?? null;
}

export function SuppressButton({ thread, className }: { thread: MailThread; className?: string }) {
  const t = useT();
  const toast = useToast();
  const address = inboundAddress(thread);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Nothing to suppress on a thread we started and nobody answered.
  if (!address) return null;

  async function suppress() {
    setBusy(true);
    try {
      await addSuppression({
        kind: "email",
        value: address!,
        source: "unsubscribe",
        reason: t("email.suppress.reason"),
        note: t("email.suppress.note", { subject: thread.subject }),
      });
      setDone(true);
      setOpen(false);
      toast(t("email.suppress.done"));
    } catch (e) {
      const status = (e as { status?: number })?.status;
      // Spoken through the toast rather than printed next to the button: a
      // sentence of red text inside a row of circles used to push the whole
      // header out of shape, and this one runs to two lines when it is the
      // permission error.
      toast(t(status === 403 ? "email.suppress.denied" : "email.suppress.error"));
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <IconBtn
        className={cn("text-amber-600", done && "is-on", className)}
        disabled={busy || done}
        onClick={() => setOpen(true)}
        title={done ? t("email.suppress.done") : t("email.suppress.action")}
        aria-label={done ? t("email.suppress.done") : t("email.suppress.action")}
      >
        {busy ? (
          <Loader2 className="size-4 animate-spin" />
        ) : done ? (
          <Check className="size-4" />
        ) : (
          <Ban className="size-4" />
        )}
      </IconBtn>

      <ConfirmDialog
        open={open}
        title={t("email.suppress.title")}
        description={t("email.suppress.confirm", { address })}
        confirmLabel={t("email.suppress.action")}
        cancelLabel={t("common.cancel")}
        variant="destructive"
        onConfirm={suppress}
        onCancel={() => setOpen(false)}
      />
    </>
  );
}
